import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MagnifyingGlass, CaretRight, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, shortDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StageBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

const STAGES = [
  ["", "All stages"],
  ["intake", "Intake / Drafting"],
  ["grant_manager_review", "Grant Manager Review"],
  ["contract_specialist_review", "Specialist Review"],
  ["ready_for_legal", "Ready for Legal"],
  ["executed", "Executed"],
];

export default function Contracts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [procurement, setProcurement] = useState("");
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [shareFlag, setShareFlag] = useState(false);
  const canCreate = ["contract_administrator", "admin"].includes(user?.role);
  const canBulk = ["contract_administrator", "admin"].includes(user?.role);
  const [selected, setSelected] = useState(new Set());
  const [staff, setStaff] = useState([]);
  const [assignField, setAssignField] = useState("assigned_contract_specialist");
  const [assignUser, setAssignUser] = useState("");

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((prev) => prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));

  const loadStaff = async () => { if (canBulk) setStaff((await api.get("/users")).data); };

  const runBulkAssign = async () => {
    if (selected.size === 0) return toast.error("Select at least one contract");
    try {
      const { data } = await api.post("/contracts/bulk-assign", {
        contract_ids: [...selected], field: assignField, user_id: assignUser || null,
      });
      toast.success(`Reassigned ${data.updated} contract(s) to ${data.assignee || "Unassigned"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadSaved = async () => setSaved((await api.get("/saved-filters", { params: { scope: "contracts" } })).data);

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (stage) params.workflow_stage = stage;
    if (procurement) params.procurement_method = procurement;
    const { data } = await api.get("/contracts", { params });
    setRows(data);
  };

  useEffect(() => {
    load();
    loadSaved();
    loadStaff();
    // eslint-disable-next-line
  }, [stage, procurement]);

  const applySaved = (f) => {
    const p = f.params || {};
    setSearch(p.search || "");
    setStage(p.workflow_stage || "");
    setProcurement(p.procurement_method || "");
  };

  const saveFilter = async () => {
    if (!filterName.trim()) return toast.error("Name your filter");
    await api.post("/saved-filters", {
      name: filterName, scope: "contracts", shared: shareFlag,
      params: { search, workflow_stage: stage, procurement_method: procurement },
    });
    toast.success("Filter saved");
    setFilterName("");
    setShareFlag(false);
    setSaveOpen(false);
    loadSaved();
  };

  const deleteSaved = async (id) => { await api.delete(`/saved-filters/${id}`); loadSaved(); };

  return (
    <div>
      <PageHeader
        overline="Contracts Module"
        title="Contracts"
        actions={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-contract-btn"><Plus size={16} className="mr-1.5" /> New Contract</Button>
              </DialogTrigger>
              <NewContractDialog onDone={(data) => { setOpen(false); if (data?.id) navigate(`/contracts/${data.id}`); else load(); }} />
            </Dialog>
          )
        }
      >
        Operational agreement records with lifecycle stages, funding, and sequential review.
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="contract-search"
            className="pl-9"
            placeholder="Search title, number, grantee…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
        </div>
        <Select value={stage || "all"} onValueChange={(v) => setStage(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-stage" className="w-56"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent>
            {STAGES.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={procurement || "all"} onValueChange={(v) => setProcurement(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-procurement" className="w-44"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="Competitive RFP">Competitive RFP</SelectItem>
            <SelectItem value="Sole Source">Sole Source</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="apply-filters">Search</Button>
      </div>

      {/* Saved filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="overline text-muted-foreground mr-1">Saved filters</span>
        {saved.length === 0 && <span className="text-xs text-muted-foreground">None yet</span>}
        {saved.map((f) => (
          <span key={f.id} data-testid={`saved-filter-${f.id}`} className="group inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border border-border bg-card text-xs hover:border-primary transition-colors">
            {!f.owned && <UsersThree size={13} className="text-primary" title={`Shared by ${f.owner_name || "team"}`} />}
            <button onClick={() => applySaved(f)} className="hover:text-primary">{f.name}</button>
            {f.owned && <button data-testid={`saved-filter-del-${f.id}`} onClick={() => deleteSaved(f.id)} className="text-muted-foreground hover:text-alert">×</button>}
          </span>
        ))}
        <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="ghost" data-testid="save-filter-btn" className="text-xs h-7"><Plus size={13} className="mr-1" /> Save current</Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-head">Save filter</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Filter name</Label>
                <Input data-testid="filter-name-input" value={filterName} onChange={(e) => setFilterName(e.target.value)} placeholder="e.g. Overdue specialist reviews" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="filter-share" checked={shareFlag} onCheckedChange={(v) => setShareFlag(!!v)} />
                Share with my {user?.role_label || "role"} team
              </label>
            </div>
            <DialogFooter><Button data-testid="filter-name-save" onClick={saveFilter}>Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {canBulk && selected.size > 0 && (
        <div data-testid="bulk-assign-bar" className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-md border border-primary/30 bg-primary/5">
          <span className="text-sm font-medium text-primary">{selected.size} selected</span>
          <Select value={assignField} onValueChange={setAssignField}>
            <SelectTrigger data-testid="bulk-field" className="w-56 h-9 bg-card"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="assigned_contract_specialist">Contract Specialist</SelectItem>
              <SelectItem value="assigned_grant_manager">Grant Manager</SelectItem>
              <SelectItem value="assigned_administrator">Administrator</SelectItem>
            </SelectContent>
          </Select>
          <Select value={assignUser || "none"} onValueChange={(v) => setAssignUser(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="bulk-user" className="w-52 h-9 bg-card"><SelectValue placeholder="Choose person" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassign</SelectItem>
              {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.role_label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" data-testid="bulk-assign-apply" onClick={runBulkAssign}>Reassign</Button>
          <Button size="sm" variant="ghost" data-testid="bulk-assign-clear" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState title="No contracts found" subtitle="Adjust filters or create a new contract to get started." />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {canBulk && <TableHead className="w-10"><Checkbox data-testid="bulk-select-all" checked={rows.length > 0 && selected.size === rows.length} onCheckedChange={toggleAll} /></TableHead>}
                <TableHead>Contract</TableHead>
                <TableHead>Grantee / Vendor</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Timeframe</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`contract-row-${c.id}`}
                  className="cursor-pointer"
                  onClick={() => navigate(`/contracts/${c.id}`)}
                >
                  {canBulk && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox data-testid={`bulk-select-${c.id}`} checked={selected.has(c.id)} onCheckedChange={() => toggleSel(c.id)} />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="font-medium text-foreground flex items-center gap-2">
                      {c.title}
                      {c.is_overdue && <span className="text-[10px] bg-alert/10 text-alert px-1.5 py-0.5 rounded-sm">OVERDUE</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{c.contract_number} · {c.program || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm">{c.grantee_vendor || "—"}</TableCell>
                  <TableCell className="text-sm font-medium">{money(c.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{shortDate(c.start_date)} → {shortDate(c.end_date)}</TableCell>
                  <TableCell><StageBadge stage={c.workflow_stage} /></TableCell>
                  <TableCell><CaretRight size={16} className="text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function NewContractDialog({ onDone }) {
  const [form, setForm] = useState({
    title: "", contract_number: "", grantee_vendor: "", program: "",
    procurement_method: "Competitive RFP", contract_type: "POS",
    amount: "", start_date: "", end_date: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.title) return toast.error("Title is required");
    setBusy(true);
    try {
      const { data } = await api.post("/contracts", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Contract created");
      onDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle className="font-head">New Contract</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-1.5">
          <Label>Contract title</Label>
          <Input data-testid="nc-title" value={form.title} onChange={set("title")} placeholder="e.g. Mobile Health Clinic Services" />
        </div>
        <div className="space-y-1.5">
          <Label>Contract number</Label>
          <Input data-testid="nc-number" value={form.contract_number} onChange={set("contract_number")} placeholder="Auto if blank" />
        </div>
        <div className="space-y-1.5">
          <Label>Grantee / Vendor</Label>
          <Input data-testid="nc-grantee" value={form.grantee_vendor} onChange={set("grantee_vendor")} />
        </div>
        <div className="space-y-1.5">
          <Label>Program</Label>
          <Input data-testid="nc-program" value={form.program} onChange={set("program")} />
        </div>
        <div className="space-y-1.5">
          <Label>Contract type</Label>
          <Select value={form.contract_type} onValueChange={set("contract_type")}>
            <SelectTrigger data-testid="nc-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="POS">Purchase of Service (POS)</SelectItem>
              <SelectItem value="FA">Financial Assistance (FA)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Amount ($)</Label>
          <Input data-testid="nc-amount" type="number" value={form.amount} onChange={set("amount")} />
        </div>
        <div className="space-y-1.5">
          <Label>Procurement method</Label>
          <Select value={form.procurement_method} onValueChange={set("procurement_method")}>
            <SelectTrigger data-testid="nc-procurement"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Competitive RFP">Competitive RFP</SelectItem>
              <SelectItem value="Sole Source">Sole Source</SelectItem>
              <SelectItem value="IDIQ">IDIQ</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Start date</Label>
          <Input data-testid="nc-start" type="date" value={form.start_date} onChange={set("start_date")} />
        </div>
        <div className="space-y-1.5">
          <Label>End date</Label>
          <Input data-testid="nc-end" type="date" value={form.end_date} onChange={set("end_date")} />
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="nc-submit" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create Contract"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
